import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/cn';

import { controlBase } from './controlStyles';

export interface SelectOption {
  value: string;
  label: string;
  /**
   * ⚠️ **What the collapsed trigger says, when the full label does not survive it** (D-1,
   * 2026-11-04). It is **optional, and eight of the nine call sites pass none** (seven files;
   * `ScheduleInterviewDialog` and `QuestionDialog` hold two each): an option without one puts its
   * `label` in the trigger, which is the string that was there before this field existed.
   *
   * It exists because truncation is not a cosmetic loss when the end of a label is the only part
   * that identifies the option. §6.3's vacancy filter measured it: a 224 px trigger holds ~174 px
   * of text, **every one** of the tenant's 95 labels is longer than that, and for 27 of them the
   * title alone overflows — so two different requisitions rendered the byte-identical
   * *"Software Engineer · Requi…"*. No width fixes that, because the overflow is not marginal.
   * What fixes it is choosing **which fact survives**, and that choice belongs to the screen that
   * knows what identifies its options, not to this component.
   *
   * **It is shown to the eye only.** `label` stays the option's one name — it is what the open
   * list renders, what the trigger's `title` tooltip recovers, and the string a caller's tests
   * assert on. A shortened trigger is never a second name for the option (BUG-088).
   *
   * ⚠️ **Only read when the caller controls `value`** (all nine sites do), because this component
   * holds no copy of the selection and must not start holding one to look an option up. An
   * uncontrolled `Select` keeps Radix's own rendering of the selected item's text and ignores this
   * field.
   */
  triggerLabel?: string;
}

interface Props extends RadixSelect.SelectProps {
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  /**
   * ⚠️ **Forwarded to the **trigger**, because `RadixSelect.Root` is a context provider and not a DOM
   * node** — anything ARIA-shaped handed to it is silently dropped (BUG-022). That is how §6.4's
   * empty-catalogue sentence came to be tied to nothing: `Field` produced a correct
   * `aria-describedby`, the dialog spread it onto this component, and it went to `Root`.
   */
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  className?: string;
}

/**
 * Radix, never a native `<select>` (§4) — it cannot be styled consistently across platforms.
 *
 * The chevron is `rule-control`, not `rule`: it is the only thing telling you the control exists,
 * so §3's "if this were invisible, would the user still know they could act here?" applies.
 */
export function Select({
  options,
  placeholder,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...props
}: Props) {
  const chosen =
    props.value === undefined ? undefined : options.find((one) => one.value === props.value);

  /**
   * ⚠️ **What the trigger paints, and the one thing that decides *who* paints it: whether the
   * caller controls `value`.** A controlled `Select` renders its own text here; an uncontrolled one
   * passes no children and Radix portals the selected `ItemText` in, exactly as before D-1. All
   * **nine** call sites are controlled and none passes `defaultValue`, so in practice this is the
   * branch taken — and the string is `option.label` at eight of them, which is the same string
   * Radix portalled.
   *
   * ⚠️ **It is deliberately not decided by "does this option ask for shortening", and that cost a
   * round to learn** (measured 2026-11-04). On that rule the filter flipped from *All vacancies*
   * (no `triggerLabel`, so Radix portalled) to a requisition (children) **in one render**, and the
   * trigger came out **empty**: two owners wrote to the same node — React's single-text-child
   * optimisation sets `textContent` on the value node, which wipes the portalled node React still
   * believes is there, and removing the portal afterwards leaves nothing.
   *
   * ⚠️ **So a defined `value` from first render to unmount is a *precondition* of this component,
   * not a fact about it.** A call site passing `value={mightBeUndefined}` flips the owner mid-life
   * and gets the blank trigger above — which is the same requirement BUG-021 already imposed for
   * its own reason (Radix decides controlled-ness by `prop !== undefined`, so a value that starts
   * `undefined` also logs *"changing from uncontrolled to controlled"*). The two docblocks that ban
   * it already are `AddCandidateDialog`'s vacancy picker and `QuestionDialog`'s
   * `?? NOTHING_CHOSEN`, and `addCandidate.test.tsx`'s *"controlled for its whole life"* suite is
   * the guard.
   *
   * `''` for a value that names no option is what Radix renders in that case too: no item matches,
   * so nothing was ever portalled. An empty value shows the placeholder, which `Value` decides.
   */
  const visible = chosen === undefined ? undefined : (chosen.triggerLabel ?? chosen.label);
  const painted = props.value === undefined ? undefined : (visible ?? '');

  /** The full label, when what is painted is **not** it — the tooltip has nothing else to add. */
  const recovered = painted === chosen?.label ? undefined : chosen?.label;

  return (
    /*
     * ⚠️ The ARIA props are **destructured out** rather than spread through: `Root` renders no element,
     * so it would swallow them (BUG-022 — a description that reached the accessibility tree of nothing).
     * They belong on the trigger, which is the control a reader focuses and a screen reader announces.
     */
    <RadixSelect.Root {...props}>
      <RadixSelect.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        /*
         * ⚠️ **A complement to the shortening, never the cure for it** (D-1). It is a native
         * tooltip: it needs a mouse, it does not reach a keyboard or a touch screen, and nothing
         * about this control may depend on it. It is here because recovering the full label costs
         * one attribute, and it is `undefined` unless something was actually shortened — a tooltip
         * repeating the text under the cursor is noise. It cannot affect the accessible name:
         * `title` is the last fallback in the name computation and every trigger here has either
         * an `aria-label` or its own contents.
         */
        title={recovered}
        className={cn(
          controlBase,
          'inline-flex w-full items-center justify-between gap-2',
          className,
        )}
      >
        {/*
         * ⚠️ **The value is truncated and the chevron never is** (2026-11-03, TASK-187). The trigger
         * is a fixed-width control and its label stopped being a short noun: §6.3's vacancy filter
         * now reads *"Firmware Engineer · Requisition 1061 · Hiring Manager …"*, because 96 of the
         * tenant's options share seven titles between them. Without this the text sets the flex
         * item's min-content width and paints out of the box over the control beside it — a flex
         * item's automatic minimum is `min-content` **unless** its overflow is hidden, which is what
         * `truncate` sets and why `min-w-0` is not needed beside it. The open list is a portal that
         * sizes to its content, so the full label is always readable where the choice is made.
         *
         * ⚠️ **Truncating was right and was not enough, and D-1 is the half it left open**
         * (2026-11-04). The clipped text is the **start** of the label, and on that filter the start
         * is the title — the fact the options share. Two different requisitions therefore painted
         * the identical pixels. So an option may now name a **shorter string for the trigger
         * alone** (`triggerLabel`), which is how the filter puts its requisition number where
         * truncation cannot reach it. Passing children to `Value` is what stops Radix portalling
         * the selected `ItemText` in here (`valueNodeHasChildren`); with none passed, it portals as
         * before. The **list** is untouched either way.
         */}
        <span className="truncate text-left">
          <RadixSelect.Value placeholder={placeholder}>{painted}</RadixSelect.Value>
        </span>
        <RadixSelect.Icon className="shrink-0">
          <ChevronDown size={16} strokeWidth={1.5} className="text-rule-control" aria-hidden />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content className="z-50 overflow-hidden rounded border border-rule bg-paper shadow-overlay">
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-4 rounded px-2 py-1.5 text-sm text-ink-900',
                  'data-[highlighted]:bg-paper-sunken data-[highlighted]:outline-none',
                )}
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator>
                  <Check size={14} strokeWidth={1.5} aria-hidden />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
